/**
 * errorService — Centralized error capture and notification.
 *
 * Pure JavaScript singleton (no React deps) so it can be imported from
 * services, hooks, and components alike.
 *
 * Usage:
 *   import { captureError, SEVERITY } from './errorService';
 *   captureError(err, 'Firestore save');
 */

// ─── Error classification ──────────────────────────────────────────────────

export const ERROR_TYPES = {
  NETWORK:    'NETWORK',    // offline, timeout, fetch failure
  AUTH:       'AUTH',       // token expired, permission denied
  API_QUOTA:  'API_QUOTA',  // Google rate-limit
  FIREBASE:   'FIREBASE',   // Firestore errors
  VALIDATION: 'VALIDATION', // invalid input
  UNKNOWN:    'UNKNOWN',    // catch-all
};

/** USER_FACING → show a toast; BACKGROUND → silent; CRITICAL → ErrorBoundary */
export const SEVERITY = {
  USER_FACING: 'user_facing',
  BACKGROUND:  'background',
  CRITICAL:    'critical',
};

/**
 * Auto-classify an error from its message / code.
 * Returns { type, severity }; callers may override severity via captureError().
 */
function _classify(error) {
  const msg  = (error?.message || '').toLowerCase();
  const code = (error?.code    || '').toLowerCase();

  if (!navigator.onLine
      || msg.includes('failed to fetch')
      || msg.includes('network')
      || msg.includes('timeout')) {
    return { type: ERROR_TYPES.NETWORK, severity: SEVERITY.BACKGROUND };
  }
  if (msg.includes('not authenticated')
      || msg.includes('permission denied')
      || msg.includes('token expired')
      || code.startsWith('auth/')) {
    return { type: ERROR_TYPES.AUTH, severity: SEVERITY.USER_FACING };
  }
  if (msg.includes('quota')
      || msg.includes('rate limit')
      || msg.includes('too many requests')) {
    return { type: ERROR_TYPES.API_QUOTA, severity: SEVERITY.USER_FACING };
  }
  if (code.startsWith('firestore')
      || code.includes('permission-denied')
      || msg.includes('firestore')) {
    return { type: ERROR_TYPES.FIREBASE, severity: SEVERITY.BACKGROUND };
  }
  if (msg.includes('invalid')
      || msg.includes('required field')
      || msg.includes('validation')) {
    return { type: ERROR_TYPES.VALIDATION, severity: SEVERITY.USER_FACING };
  }
  return { type: ERROR_TYPES.UNKNOWN, severity: SEVERITY.BACKGROUND };
}

// ─── In-memory store (max 50 entries, newest first) ───────────────────────

const MAX_STORED = 50;
const _errors    = [];     // ErrorEntry[]
const _listeners = new Set(); // Set<(entry: ErrorEntry | null) => void>

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * captureError — Record an error and notify all listeners.
 *
 * @param {Error|unknown} error
 * @param {string}        context        — human-readable location (e.g. 'Firestore save')
 * @param {string|null}   overrideSeverity — override auto-detected severity
 * @returns {object} The created ErrorEntry
 */
export function captureError(error, context = '', overrideSeverity = null) {
  const { type, severity } = _classify(error);

  const entry = {
    id:        `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: new Date().toISOString(),
    type,
    severity:  overrideSeverity ?? severity,
    message:   error?.message || String(error),
    context,
    // Stack traces hidden in production
    stack: process.env.NODE_ENV !== 'production' ? (error?.stack ?? null) : null,
  };

  _errors.unshift(entry);
  if (_errors.length > MAX_STORED) _errors.pop();

  // In development, mirror to console so DevTools still works
  if (process.env.NODE_ENV !== 'production') {
    console.error(`[ErrorService][${entry.type}] ${context}:`, error);
  }

  _listeners.forEach(fn => fn(entry));
  return entry;
}

/**
 * getLastErrors — Return the N most-recent errors (newest first).
 * @param {number} n
 * @returns {object[]}
 */
export function getLastErrors(n = 10) {
  return _errors.slice(0, n);
}

/**
 * clearErrors — Wipe the error log and notify listeners with null.
 */
export function clearErrors() {
  _errors.length = 0;
  _listeners.forEach(fn => fn(null));
}

/**
 * subscribe — Register a listener that receives each new ErrorEntry.
 * Receives null when clearErrors() is called.
 * @param {function} fn
 * @returns {function} Unsubscribe function
 */
export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}
